import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AdminService } from '../../services/adminService';

/**
 * Facturation d'un établissement (PLAN_IMPL_COMPTE_ETABLISSEMENT CE-8).
 *
 * Le prix se NÉGOCIE établissement par établissement : on pose ici le prix HT par licence, la
 * remise et la TVA, et les informations du client. « Émettre » fait émettre la facture par Stripe
 * (numéro, PDF, page de paiement par carte ou virement) ; le paiement la passe « payée » par
 * webhook. Une facture émise ne se retire pas : on l'annule par un AVOIR, dans Stripe.
 */

const CHAMPS = [
  ['raisonSociale', 'Raison sociale', { required: true }],
  ['adresseLigne1', 'Adresse', { required: true }],
  ['adresseLigne2', "Complément d'adresse"],
  ['codePostal', 'Code postal', { required: true, demi: true }],
  ['ville', 'Ville', { required: true, demi: true }],
  ['siret', 'SIRET', { demi: true }],
  ['tvaIntracom', 'N° TVA intracommunautaire', { demi: true, helper: 'Si le client en a un' }],
  ['emailFacturation', 'E-mail de facturation', { required: true }],
  ['referenceCommande', 'Référence du bon de commande', { helper: 'Portée sur la facture' }],
];

const euros = (c) => (c == null ? '—' : (c / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }));
const enCentimes = (v) => {
  const t = String(v ?? '').replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  const n = Math.round(Number(t) * 100);
  return Number.isFinite(n) ? n : NaN;
};
const messageErreur = (e) => e?.response?.data?.message || e?.message || 'Erreur';
const STATUTS = { emise: ['Émise', 'warning'], payee: ['Payée', 'success'], annulee: ['Annulée', 'default'] };

export default function EtablissementFacturation({ etablissement, onFermer, onChange }) {
  const [donnees, setDonnees] = useState(null);
  const [f, setF] = useState({});
  const [facture, setFacture] = useState({ licences: '', libelle: '', envoyer: true });
  const [erreur, setErreur] = useState(null);
  const [info, setInfo] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    if (!etablissement) return;
    setErreur(null);
    try {
      const d = await AdminService.getFacturationEtablissement(etablissement.id);
      setDonnees(d);
      const c = d.conditions;
      setF({
        ...Object.fromEntries(CHAMPS.map(([k]) => [k, c[k] ?? ''])),
        prix: c.prixLicenceHtCentimes == null ? '' : String(c.prixLicenceHtCentimes / 100).replace('.', ','),
        remisePct: c.remisePct ?? 0,
        tauxTvaPct: c.tauxTvaPct ?? 20,
      });
    } catch (e) {
      setErreur(messageErreur(e));
    }
  }, [etablissement]);

  useEffect(() => {
    setDonnees(null);
    setInfo(null);
    setFacture({ licences: '', libelle: '', envoyer: true });
    charger();
  }, [charger]);

  const champ = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const enregistrer = async () => {
    const prix = enCentimes(f.prix);
    if (Number.isNaN(prix)) { setErreur('Prix par licence illisible'); return; }
    setEnCours(true);
    setErreur(null);
    try {
      await AdminService.modifierConditionsCommerciales(etablissement.id, {
        ...Object.fromEntries(CHAMPS.map(([k]) => [k, f[k]])),
        prixLicenceHtCentimes: prix,
        remisePct: Number(String(f.remisePct).replace(',', '.')),
        tauxTvaPct: Number(String(f.tauxTvaPct).replace(',', '.')),
      });
      setInfo({ severity: 'success', texte: 'Conditions enregistrées.' });
      await charger();
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  const emettre = async () => {
    const n = facture.licences === '' ? donnees.conditions.licences : Number(facture.licences);
    if (!window.confirm(`Émettre une facture de ${n} licence(s) pour « ${donnees.conditions.raisonSociale} » ?\nUne facture émise ne se retire pas (un avoir l'annule, dans Stripe).`)) return;
    setEnCours(true);
    setErreur(null);
    try {
      await AdminService.emettreFactureEtablissement(etablissement.id, {
        licences: facture.licences === '' ? undefined : Number(facture.licences),
        libelle: facture.libelle || undefined,
        envoyer: facture.envoyer,
      });
      setInfo({ severity: 'success', texte: facture.envoyer ? 'Facture émise et envoyée au client par Stripe.' : 'Facture émise (non envoyée).' });
      setFacture({ licences: '', libelle: '', envoyer: true });
      await charger();
      onChange?.();
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  const actualiser = async (id) => {
    try {
      await AdminService.actualiserFactureEtablissement(id);
      await charger();
      onChange?.();
    } catch (e) {
      setErreur(messageErreur(e));
    }
  };

  const c = donnees?.conditions;
  return (
    <Dialog open={Boolean(etablissement)} onClose={onFermer} maxWidth="md" fullWidth>
      <DialogTitle>Facturation — {etablissement?.nom}</DialogTitle>
      <DialogContent>
        {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
        {info && <Alert severity={info.severity} onClose={() => setInfo(null)} sx={{ mb: 2 }}>{info.texte}</Alert>}
        {!donnees ? <CircularProgress /> : (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>Client</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {CHAMPS.map(([k, label, o = {}]) => (
                  <TextField key={k} label={label} value={f[k] ?? ''} onChange={champ(k)} size="small"
                    required={o.required} helperText={o.helper} sx={{ gridColumn: o.demi ? 'auto' : '1 / -1' }} />
                ))}
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle1" gutterBottom>Conditions négociées</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Prix HT par licence (€)" value={f.prix ?? ''} onChange={champ('prix')} size="small" required />
                <TextField label="Remise (%)" value={f.remisePct ?? ''} onChange={champ('remisePct')} size="small" />
                <TextField label="TVA (%)" value={f.tauxTvaPct ?? ''} onChange={champ('tauxTvaPct')} size="small" />
              </Stack>
              <Button sx={{ mt: 2 }} variant="outlined" onClick={enregistrer} disabled={enCours}>Enregistrer les conditions</Button>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle1" gutterBottom>Émettre une facture</Typography>
              {donnees.apercu && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {c.licences} licences × {euros(c.prixLicenceHtCentimes)}
                  {donnees.apercu.remise > 0 && <> − remise {euros(donnees.apercu.remise)}</>}
                  {' '}= {euros(donnees.apercu.ht)} HT · TVA {euros(donnees.apercu.tva)} · <strong>{euros(donnees.apercu.ttc)} TTC</strong>
                  {' '}— paiement à {donnees.delaiPaiementJours} jours
                </Typography>
              )}
              {!donnees.stripeConfigure && <Alert severity="warning" sx={{ mb: 1 }}>Stripe n&apos;est pas configuré sur ce serveur.</Alert>}
              {donnees.manques.length > 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>Pour facturer, il manque : {donnees.manques.join(', ')} (enregistrer d&apos;abord).</Alert>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField label="Licences facturées" type="number" size="small" value={facture.licences}
                  placeholder={String(c.licences)} onChange={(e) => setFacture((x) => ({ ...x, licences: e.target.value }))}
                  InputLabelProps={{ shrink: true }} helperText="Vide = toutes les licences" />
                <TextField label="Libellé" size="small" value={facture.libelle} fullWidth
                  onChange={(e) => setFacture((x) => ({ ...x, libelle: e.target.value }))}
                  helperText="Vide = « Licences élève DictoQuest <offre> — du … au … »" />
              </Stack>
              <FormControlLabel
                control={<Checkbox checked={facture.envoyer} onChange={(e) => setFacture((x) => ({ ...x, envoyer: e.target.checked }))} />}
                label="Envoyer la facture au client par e-mail (Stripe)" />
              <Box>
                <Button variant="contained" onClick={emettre}
                  disabled={enCours || donnees.manques.length > 0 || !donnees.stripeConfigure}>
                  {enCours ? <CircularProgress size={20} /> : 'Émettre la facture'}
                </Button>
              </Box>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle1" gutterBottom>Factures</Typography>
              {donnees.factures.length === 0 ? <Typography variant="body2">Aucune facture.</Typography> : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>N°</TableCell>
                      <TableCell>Émise le</TableCell>
                      <TableCell align="right">Licences</TableCell>
                      <TableCell align="right">HT</TableCell>
                      <TableCell align="right">TTC</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {donnees.factures.map((x) => (
                      <TableRow key={x.id}>
                        <TableCell>{x.numero || '—'}</TableCell>
                        <TableCell>{new Date(x.emiseLe).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell align="right">{x.licences}</TableCell>
                        <TableCell align="right">{euros(x.montantHtCentimes)}</TableCell>
                        <TableCell align="right">{euros(x.ttcCentimes)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={STATUTS[x.statut]?.[0] || x.statut} color={STATUTS[x.statut]?.[1] || 'default'} />
                          {x.payeeLe && <Typography variant="caption" sx={{ ml: 1 }}>{new Date(x.payeeLe).toLocaleDateString('fr-FR')}</Typography>}
                        </TableCell>
                        <TableCell align="right">
                          {x.urlPdf && <Link href={x.urlPdf} target="_blank" rel="noopener" sx={{ mr: 1 }}>PDF</Link>}
                          {x.urlFacture && <Link href={x.urlFacture} target="_blank" rel="noopener" sx={{ mr: 1 }}>Page de paiement</Link>}
                          <Button size="small" onClick={() => actualiser(x.id)}>Actualiser</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Un paiement par virement se marque « payé » dans Stripe ; l&apos;annulation d&apos;une facture émise passe par un avoir, dans Stripe.
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFermer}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}
